<template>
  <div class="p-6 bg-white rounded-lg">
    <h3 class="text-2xl font-medium mb-4 pb-2 border-b">
      {{ module.title }}
    </h3>

    <p class="text-base leading-tight mb-6" v-html="nl2br(module.settings.description)"></p>

    <form @submit="sendRequest">
      <div
        v-for="(element, index) in module.settings.elements"
        :key="index"
        class="mb-6"
      >
        <label class="label-xs">
          {{ element.label }}
        </label>

        <input
          v-if="element.type === 'text'"
          v-model="formData[element.label]"
          type="text"
          :placeholder="element.placeholder"
          class="w-full input-sm mb-4"
          :required="element.required"
        >

        <textarea
          v-if="element.type === 'textarea'"
          v-model="formData[element.label]"
          type="text"
          :placeholder="element.placeholder"
          class="w-full input-sm mb-4"
          rows="4"
          :required="element.required"
        />

        <pl-select
          v-if="element.type === 'select'"
          v-model="formData[element.label]"
          class="w-full"
          select-class="select-sm"
          :required="element.required"
        >
          <option
            :value="null"
            disabled
            selected
          >
            Please Select
          </option>
          <option
            v-for="(option, index) in element.options"
            :key="index"
          >
            {{ option }}
          </option>
        </pl-select>
      </div>
      <div class="w-full mb-8" v-if="module.settings.include_upload">
        <label>
          {{ module.settings.upload_title }} <span v-if="module.settings.include_upload_required" class="text-red">*</span>
        </label>
        <div v-if="previewFiles.length" class="my-2">
          <ul class="list-reset">
            <li v-for="(previewFile, key) in previewFiles" :key="key" class="py-1">
              <a class="cursor-pointer no-underline" :href="previewFile.url" target="_blank">Click to view File {{key + 1}} <span v-if="previewFile.name">({{previewFile.name}})</span></a>
              <span class="text-red ml-2 cursor-pointer font-bold" @click="removeFile(previewFile, key)">X</span>
            </li>
          </ul>
        </div>
        <div class="text-red" v-if="uploadError && previewFiles.length === 0">{{uploadError}}</div>
        <div class="text-red" v-if="uploadFileSizeError && previewFiles.length > 0">{{uploadFileSizeError}}</div>
        <div class="my-2">
          <upload-dropzone @change="uploadAssets($event)" accept="*" />
        </div>
      </div>
      <button
        type="submit"
        class="mb-2 p-3 inline-block no-underline bg-secondary button-text-color text-center text-sm w-full rounded uppercase"
      >
        {{ module.settings.action }}
      </button>
    </form>
  </div>
</template>

<script>
import api from '@/http'
import http from 'axios'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import { nl2br } from '@/helpers'
import UploadDropzone from '@/components/UploadDropzone'

export default {
  components: { UploadDropzone },
  props: ['module'],
  data () {
    return {
      formData: {},
      formFile: null,
      uploadError: null,
      previewFiles: [],
      uploadedFileSize: 0,
      uploadFileSizeError: null
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile'])
  },
  methods: {
    nl2br,

    ...mapActions([
      'displayToastNotification'
    ]),

    buildFormData () {
      this.module.settings.elements.forEach(element => {
        this.$set(this.formData, element.label, null)
      })
    },
    sendRequest (e) {
      e.preventDefault()
      if (this.module.settings.include_upload_required && this.previewFiles.length === 0) {
        this.uploadError = 'Please upload at least one file'
        return
      }
      this.uploadError = null
      this.uploadFileSizeError = null
      this.uploadedFileSize = 0
      this.$set(this.formData, 'office_id', this.selectedOffice.id)
      api
        .post(`/form-module/${this.module.id}`, this.formData)
        .then(response => {
          this.buildFormData()
          if (this.module.settings.include_upload) {
            this.deleteAssets()
            this.previewFiles = []
          }
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Successfully Sent',
            info: `${this.module.title} has been successfully sent`
          })
        })
        .catch(e => {
          this.displayToastNotification({
            type: 'warning',
            icon: 'fa-bell',
            title: 'Unable to send your request',
            info: 'We\'ve been unable to send the form, please try again shortly.'
          })
        })
    },
    deleteAssets () {
      api.delete(`/form-module/${this.module.id}/delete-assets`).then((response) => {
        if (response.data) {
          for (let index in response.data) {
            let asset = response.data[index]
            this.previewFiles.push({
              'id': asset.id,
              'url': asset.url,
              'name': asset.name
            })
          }
        }
      })
    },
    uploadAssets (files) {
      for (let i = 0; i < files.length; i++) {
        this.uploadAsset(files[i])
      }
    },
    uploadAsset (formFile) {
      // If the ticket type is email, then allowed file size is 10 MB
      let maxFileSize = 10240000
      let allowedSize = (maxFileSize / (1024 * 1024)).toFixed(2)
      let type = 'MB'
      // If the ticket type is houbspot, then allowed file size is 2 GB
      if (this.module.settings.ticket_type === '2') {
        maxFileSize = 2147483648
        allowedSize = (maxFileSize / (1024 * 1024 * 1024)).toFixed(2)
        type = 'GB'
      }
      this.uploadFileSizeError = null
      this.uploadedFileSize += formFile.size
      if (this.uploadedFileSize > maxFileSize) {
        this.uploadFileSizeError = `The size of the file(s) uploaded is too large. The maximum size is ${parseFloat(allowedSize).toFixed() + type}`
        this.uploadedFileSize -= formFile.size
        return
      }
      let fileName = `form-module-images/${moment(new Date()).format('YYYYMMDD')}-${this.selectedOffice.id}-${this.profile.id}-${formFile.name}`
      fileName = fileName.replace(/\s/g, '')
      let headers
      const extension = formFile.name.split('.').pop()
      api.post('signed-upload', {
        'disk': 's3-private',
        'extension': extension,
        'key': fileName,
        'content_type': formFile.type
      }).then((response) => {
        headers = response.data.headers
        if ('Host' in headers) {
          delete headers.Host
        }
        http.put(response.data.url, formFile, {
          headers: headers
        })
      })

      let data = {
        'fileName': fileName,
        'id': this.module.id
      }
      api.post(`/form-module/${this.module.id}/upload-asset`, data).then((response) => {
        this.previewFiles.push({
          'id': response.data.id,
          'url': response.data.url,
          'name': formFile.name
        })
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check',
          title: 'Successfully Sent',
          info: `File of ${this.module.title} has been successfully uploaded.`
        })
      }, (errors) => {
        this.displayToastNotification({
          type: 'warning',
          icon: 'fa-bell',
          title: 'Unable to upload file',
          info: 'We\'ve been unable to upload file, please try again shortly.'
        })
      })
    },
    removeFile (previewFile, index) {
      let result = confirm(' Are you sure you want to remove this file?')
      if (result) {
        api.delete(`/form-module/delete-asset/${previewFile.id}`).then((response) => {
          this.previewFiles.splice(index, 1)
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Successfully Deleted',
            info: `File ${previewFile.name} has been successfully deleted.`
          })
        })
      }
    }
  },

  created () {
    this.buildFormData()
    if (this.module.settings.include_upload) {
      this.deleteAssets()
    }
  }
}
</script>
